import { pgTable, uuid, text, integer, timestamp, jsonb, index } from "drizzle-orm/pg-core";

// --- Brand Status ---

export const BRAND_STATUSES = ["pending", "verified", "rejected", "suspended"] as const;
export type BrandStatus = (typeof BRAND_STATUSES)[number];

// --- Campaign Status ---

export const TEN_DLC_CAMPAIGN_STATUSES = ["pending", "registered", "rejected", "suspended", "carrier_propagated"] as const;
export type TenDlcCampaignStatus = (typeof TEN_DLC_CAMPAIGN_STATUSES)[number];

// --- 10DLC Brand Registration ---

/** TCR brand registration — one per tenant */
export const tenDlcBrands = pgTable("ten_dlc_brands", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  tcrBrandId: text("tcr_brand_id"),  // assigned by TCR after submission
  businessName: text("business_name").notNull(),
  ein: text("ein"),  // US tax ID stored as enc:v1 envelope text via @ease/crypto; no schema migration required
  country: text("country").notNull(),
  status: text("status").default("pending").notNull(),
  vettingScore: integer("vetting_score"),  // TCR trust score (0-100)
  rejectionReason: text("rejection_reason"),
  submittedAt: timestamp("submitted_at", { withTimezone: true }),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_ten_dlc_brands_tenant").on(table.tenantId),
  index("idx_ten_dlc_brands_status").on(table.status),
]);

// --- 10DLC Campaign Registration ---

/** TCR campaign registration — one per use case per tenant */
export const tenDlcCampaigns = pgTable("ten_dlc_campaigns", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  brandId: uuid("brand_id").notNull(),  // FK to tenDlcBrands
  tcrCampaignId: text("tcr_campaign_id"),  // assigned by TCR
  useCase: text("use_case").notNull(),  // MARKETING, CUSTOMER_CARE, 2FA, etc.
  description: text("description").notNull(),  // use case description for TCR
  sampleMessages: jsonb("sample_messages").default([]).notNull(),  // required by TCR
  helpMessage: text("help_message").notNull(),  // HELP reply content
  optOutMessage: text("opt_out_message").notNull(),  // STOP reply content
  status: text("status").default("pending").notNull(),
  throughputLimit: integer("throughput_limit"),  // messages per second (from carrier)
  rejectionReason: text("rejection_reason"),
  submittedAt: timestamp("submitted_at", { withTimezone: true }),
  registeredAt: timestamp("registered_at", { withTimezone: true }),
  carrierPropagatedAt: timestamp("carrier_propagated_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_ten_dlc_campaigns_tenant").on(table.tenantId),
  index("idx_ten_dlc_campaigns_brand").on(table.brandId),
  index("idx_ten_dlc_campaigns_status").on(table.status),
]);

// --- Phone Number Registry ---

/** Per-tenant phone number registry */
export const tenantPhoneNumbers = pgTable("tenant_phone_numbers", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  phoneNumber: text("phone_number").notNull(),  // E.164
  providerId: text("provider_id").notNull(),  // telnyx, sinch, twilio
  numberType: text("number_type").notNull(),  // long_code, toll_free, short_code
  country: text("country").notNull(),
  campaignId: uuid("campaign_id"),  // FK to tenDlcCampaigns (for 10DLC numbers)
  healthScore: integer("health_score").default(100).notNull(),
  status: text("status").default("active").notNull(),  // active, blocked, quarantined, retired
  lastBlockAt: timestamp("last_block_at", { withTimezone: true }),
  carrierBlockCount: integer("carrier_block_count").default(0).notNull(),
  provisionedAt: timestamp("provisioned_at", { withTimezone: true }).defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
}, (table) => [
  index("idx_phone_numbers_tenant").on(table.tenantId),
  index("idx_phone_numbers_status").on(table.tenantId, table.status),
  index("idx_phone_numbers_health").on(table.tenantId, table.healthScore),
]);
